import { useTranslation } from 'react-i18next';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { cn } from '../../../lib/utils';

interface PlanCardProps {
  plan: {
    id: 'free' | 'pro' | 'enterprise';
    name: string;
    price: number;
    interval: 'month' | 'year';
    features: string[];
  };
  isCurrentPlan: boolean;
  isLoading: boolean;
  onSelect: () => void;
  isUpgrade: boolean;
  isCanceled?: boolean;
  isRecommended?: boolean;
}

export function PlanCard({
  plan,
  isCurrentPlan,
  isLoading,
  onSelect,
  isUpgrade,
  isCanceled,
  isRecommended = plan.id === 'pro',
}: PlanCardProps) {
  const { t } = useTranslation();

  return (
    <Card
      className={cn(
        'relative transition-all duration-300 hover:shadow-brand-glow',
        isCurrentPlan && 'border-primary',
        isRecommended && 'ring-2 ring-brand-purple-500 shadow-lg scale-105'
      )}
    >
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-gradient-brand text-white text-xs font-medium px-3 py-1 rounded-full">
          <Sparkles className="h-3 w-3" />
          Recommended
        </div>
      )}
      <CardHeader className="pt-6">
        <CardTitle className="flex items-center justify-between">
          <span className={cn(isRecommended && 'text-gradient-brand')}>{plan.name}</span>
          {isCurrentPlan && (
            <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
              {t('billing.currentPlan')}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          <span className="text-3xl font-mono font-bold tracking-tight text-foreground">
            ${plan.price}
          </span>
          <span className="text-sm font-normal text-muted-foreground">/{plan.interval}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="space-y-2">
          {plan.features.map((feature, index) => (
            <li
              key={index}
              className="flex items-center gap-2 text-sm opacity-0 animate-stagger-reveal"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <Check className="h-4 w-4 text-primary flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
        <Button
          className="w-full"
          variant={isCurrentPlan ? 'outline' : 'default'}
          disabled={isCurrentPlan && !isCanceled}
          onClick={onSelect}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isCurrentPlan
            ? isCanceled
              ? t('billing.reactivate')
              : t('billing.currentPlan')
            : isUpgrade
              ? t('billing.upgrade')
              : t('billing.downgrade')}
        </Button>
      </CardContent>
    </Card>
  );
}
